/**
 * Mobile Labour Registration with 3 tabs.
 *
 *   1. Add One     — single-labour form (Name, Aadhar, Mobile, DOB all mandatory).
 *                    On save → auto-navigates to the face-enrol kiosk for that labour.
 *   2. Upload List — Excel bulk import.
 *   3. List        — Searchable list of all labour, with a "Enrol Face" CTA next to
 *                    each entry that hasn't been face-enrolled yet.
 *
 * Single-plant RMC — no project assignment, no subcontractor linking, no
 * inter-site transfer (all removed in the RMC simplification; ELR has no
 * casual/contract labour and only one site).
 */
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, UserPlus, Upload, Download, Loader2, CheckCircle2, AlertTriangle, Camera, Search, ChevronRight, Zap, Filter } from 'lucide-react';
import axios from 'axios';
import { apiGet, apiPost, API } from '../../lib/api';

const initialForm = {
  name: '', aadhar_number: '', mobile: '', dob: '',
  gender: '', trade: '', designation: '', daily_wage: '', address: '',
  supervisor_name: '',
};

export default function MobileLabourRegister() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('add');           // 'add' | 'upload' | 'list'
  const [masters, setMasters] = useState({});
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [labourList, setLabourList] = useState([]);
  const [listQuery, setListQuery] = useState('');
  const [listLoading, setListLoading] = useState(false);
  const [listFilters, setListFilters] = useState({ trade: '' });
  const [showListFilters, setShowListFilters] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    apiGet('/config-masters').then((r) => setMasters(r.masters || {})).catch(() => {});
  }, []);

  const loadList = () => {
    setListLoading(true);
    const params = new URLSearchParams();
    if (listFilters.trade) params.set('trade', listFilters.trade);
    apiGet(`/labour-master${params.toString() ? '?' + params.toString() : ''}`).then((r) => {
      setLabourList(Array.isArray(r) ? r : (r?.items || r?.labour || []));
    }).catch(() => toast.error('Could not load labour list')).finally(() => setListLoading(false));
  };
  useEffect(() => { if (tab === 'list') loadList(); }, [tab, listFilters]); // eslint-disable-line

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (form.aadhar_number.replace(/\D/g, '').length !== 12) { toast.error('Valid 12-digit Aadhar required'); return; }
    if (form.mobile.replace(/\D/g, '').length < 10) { toast.error('Valid 10-digit mobile required'); return; }
    if (!form.dob) { toast.error('Date of Birth is required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        aadhar_number: form.aadhar_number.replace(/\D/g, ''),
        mobile: form.mobile.replace(/\D/g, ''),
        daily_wage: form.daily_wage ? parseFloat(form.daily_wage) : null,
      };
      const res = await apiPost('/labour-master', payload);
      const newId = res?.id || res?.labour?.id;
      toast.success(`${form.name} registered · capture face now`);
      // Mandatory face-enrol step right after creation
      const name = encodeURIComponent(form.name);
      navigate(`/m/labour/enroll?labour_id=${newId}&name=${name}&from=register`);
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : (msg?.message || 'Could not save'));
    } finally { setSaving(false); }
  };

  const downloadTemplate = async () => {
    try {
      const token = localStorage.getItem('access_token') || '';
      const r = await axios.get(`${API}/labour-master/template.xlsx`, {
        responseType: 'blob', headers: { Authorization: `Bearer ${token}` },
      });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'labour-template.xlsx'; a.click();
      URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch { toast.error('Template download failed'); }
  };

  const uploadExcel = async (file) => {
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData(); fd.append('file', file);
      const token = localStorage.getItem('access_token') || '';
      const r = await axios.post(`${API}/labour-master/import`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(r.data);
      toast.success(`Imported ${r.data.created} · skipped ${r.data.skipped}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Import failed'); }
    finally { setImporting(false); }
  };

  const enrolFace = (labour, force = false) => {
    const name = encodeURIComponent(labour.name);
    const forceParam = force ? '&force=1' : '';
    navigate(`/m/labour/enroll?labour_id=${labour.id}&name=${name}&from=list${forceParam}`);
  };

  return (
    <div className="min-h-screen bg-[#FDFDFD]">
      <header className="sticky top-0 z-20 bg-white border-b border-slate-200 px-3 py-3 flex items-center justify-between">
        <button onClick={() => navigate('/m')} className="flex items-center gap-1 text-xs font-display font-bold uppercase tracking-wider text-slate-600" data-testid="m-labour-reg-back">
          <ArrowLeft size={14} /> Back
        </button>
        <h1 className="font-display font-bold text-sm uppercase tracking-wide flex items-center gap-1.5">
          <UserPlus size={15} /> Labour
        </h1>
        <div className="w-12" />
      </header>

      <div className="flex border-b border-slate-200 bg-white">
        {[
          { k: 'add', label: 'Add One' },
          { k: 'upload', label: 'Upload List' },
          { k: 'list', label: `List${labourList.length ? ` · ${labourList.length}` : ''}` },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className={`flex-1 py-3 text-xs font-display font-bold uppercase tracking-wider ${tab === t.k ? 'text-[#1F7A6E] border-b-2 border-[#1F7A6E]' : 'text-slate-500'}`}
            data-testid={`m-labour-tab-${t.k}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="p-3">
        {tab === 'add' && (
          <form onSubmit={submit} className="space-y-3" data-testid="m-labour-add-form">
            <MField label="Full Name *" value={form.name} onChange={upd('name')} testid="m-labour-name" />
            <MField label="Aadhar Number (12 digits) *" value={form.aadhar_number} onChange={upd('aadhar_number')} testid="m-labour-aadhar" inputMode="numeric" maxLength={12} />
            <MField label="Mobile (10 digits) *" value={form.mobile} onChange={upd('mobile')} testid="m-labour-mobile" inputMode="tel" maxLength={10} />
            <div>
              <label className="text-[10px] font-display font-bold tracking-wider uppercase text-slate-500">Date of Birth *</label>
              <input type="date" value={form.dob} onChange={upd('dob')} max={new Date().toISOString().slice(0,10)}
                className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm mt-1 focus:outline-none focus:border-[#1F7A6E]"
                data-testid="m-labour-dob" />
            </div>
            <MField label="Supervisor Name" value={form.supervisor_name} onChange={upd('supervisor_name')} testid="m-labour-supervisor" />
            <div className="grid grid-cols-2 gap-3">
              <MSelect label="Gender" value={form.gender} onChange={upd('gender')} options={(masters.labour_genders || []).map((v) => ({ value: v, label: v }))} testid="m-labour-gender" />
              <MSelect label="Trade" value={form.trade} onChange={upd('trade')} options={(masters.labour_trades || []).map((v) => ({ value: v, label: v }))} testid="m-labour-trade" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <MSelect label="Designation" value={form.designation} onChange={upd('designation')} options={(masters.labour_designations || []).map((v) => ({ value: v, label: v }))} testid="m-labour-designation" />
              <MField label="Daily Wage (₹)" value={form.daily_wage} onChange={upd('daily_wage')} testid="m-labour-wage" inputMode="decimal" />
            </div>
            <div>
              <label className="text-[10px] font-display font-bold tracking-wider uppercase text-slate-500">Address</label>
              <textarea value={form.address} onChange={upd('address')} rows={2} className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm mt-1 focus:outline-none focus:border-[#1F7A6E]" data-testid="m-labour-address" />
            </div>

            <div className="bg-cyan-50 border border-cyan-200 rounded-sm p-2.5 text-[11px] text-cyan-900 flex items-start gap-2">
              <Camera size={13} className="shrink-0 mt-0.5" />
              <div>After saving, you&apos;ll be taken to the camera to capture the labour&apos;s face — this is mandatory so that the kiosk can recognise them at the gate.</div>
            </div>

            <button type="submit" disabled={saving}
              className="w-full bg-[#1F7A6E] text-white rounded-sm px-4 py-3 text-sm font-display font-bold uppercase tracking-wider hover:bg-[#14504E] disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="m-labour-save">
              {saving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : <><UserPlus size={14} /> Register & Capture Face</>}
            </button>
          </form>
        )}

        {tab === 'upload' && (
          <div className="space-y-3" data-testid="m-labour-upload-pane">
            <div className="bg-white border border-slate-200 rounded-sm p-4 text-center">
              <Upload size={32} className="mx-auto text-[#1F7A6E] mb-2" />
              <h3 className="font-display font-bold text-sm mb-1">Bulk Import from Excel</h3>
              <p className="text-xs text-slate-500 mb-3">Download the template, fill labour details, then upload. Existing Aadhar numbers will be skipped.</p>
              <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => uploadExcel(e.target.files?.[0])} data-testid="m-labour-file-input" />
              <div className="space-y-2">
                <button onClick={downloadTemplate} className="w-full border border-slate-300 rounded-sm px-3 py-2.5 text-xs font-display font-bold uppercase tracking-wider text-slate-700 hover:bg-slate-50 flex items-center justify-center gap-2" data-testid="m-labour-template-btn">
                  <Download size={13} /> Download Excel Template
                </button>
                <button onClick={() => fileRef.current?.click()} disabled={importing}
                  className="w-full bg-[#1F7A6E] text-white rounded-sm px-3 py-2.5 text-xs font-display font-bold uppercase tracking-wider hover:bg-[#14504E] disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="m-labour-upload-btn">
                  {importing ? <><Loader2 size={13} className="animate-spin" /> Uploading…</> : <><Upload size={13} /> Choose Excel File</>}
                </button>
              </div>
            </div>

            {importResult && (
              <>
                <div className="bg-white border border-slate-200 rounded-sm p-3" data-testid="m-labour-import-result">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 size={16} className="text-emerald-600" />
                    <span className="font-display font-bold text-sm">Import Result</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <Stat label="Created" value={importResult.created} accent="text-emerald-700" />
                    <Stat label="Skipped" value={importResult.skipped} accent="text-amber-700" />
                  </div>
                  {importResult.errors?.length > 0 && (
                    <div className="mt-3">
                      <div className="text-[10px] font-display font-bold uppercase tracking-wider text-rose-600 flex items-center gap-1 mb-1">
                        <AlertTriangle size={11} /> Errors ({importResult.errors.length})
                      </div>
                      <ul className="text-[11px] text-slate-600 space-y-1 max-h-40 overflow-y-auto">
                        {importResult.errors.slice(0, 20).map((err, i) => (
                          <li key={i} className="bg-rose-50 px-2 py-1 rounded-sm">{typeof err === 'string' ? err : JSON.stringify(err)}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                {importResult.created > 0 && (
                  <button onClick={() => setTab('list')} className="w-full bg-emerald-600 text-white rounded-sm px-3 py-2.5 text-xs font-display font-bold uppercase tracking-wider hover:bg-emerald-700 flex items-center justify-center gap-2" data-testid="m-labour-go-list">
                    Now enrol faces for the {importResult.created} new labour → <ChevronRight size={13} />
                  </button>
                )}
              </>
            )}

            <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-[11px] text-amber-900">
              <strong>Template tips:</strong> Aadhar must be 12 digits. Mobile 10 digits. DOB in YYYY-MM-DD.
            </div>
          </div>
        )}

        {tab === 'list' && (
          <div className="space-y-3" data-testid="m-labour-list-pane">
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={listQuery}
                onChange={(e) => setListQuery(e.target.value)}
                placeholder="Search by name, Aadhar, mobile…"
                className="w-full bg-white border border-slate-300 rounded-sm pl-8 pr-10 py-2 text-sm focus:outline-none focus:border-[#1F7A6E]"
                data-testid="m-labour-list-search"
              />
              <button
                onClick={() => setShowListFilters((v) => !v)}
                className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded ${showListFilters ? 'text-[#1F7A6E]' : 'text-slate-400'}`}
                data-testid="m-labour-filter-toggle"
              >
                <Filter size={14} />
              </button>
            </div>

            {/* Filters panel */}
            {showListFilters && (
              <div className="bg-white border border-slate-200 rounded-sm p-3 space-y-2" data-testid="m-labour-filters">
                <div className="text-[10px] font-display font-bold uppercase tracking-wider text-slate-500 mb-1">Filters</div>
                <select value={listFilters.trade} onChange={(e) => setListFilters({ ...listFilters, trade: e.target.value })} className="w-full border border-slate-200 rounded-sm px-3 py-2 text-xs bg-white" data-testid="m-labour-filter-trade">
                  <option value="">All Trades</option>
                  {(masters.labour_trades || []).map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <button onClick={() => setListFilters({ trade: '' })} className="text-xs text-slate-500 underline">Clear filters</button>
              </div>
            )}

            {listLoading && (
              <div className="flex items-center justify-center py-8"><Loader2 size={24} className="animate-spin text-[#1F7A6E]" /></div>
            )}
            {!listLoading && labourList.length === 0 && (
              <div className="bg-white border border-slate-200 rounded-sm p-6 text-center text-xs text-slate-500">
                No labour registered yet. Tap <strong>Add One</strong> or <strong>Upload List</strong> to start.
              </div>
            )}
            {!listLoading && labourList.length > 0 && (() => {
              const q = listQuery.trim().toLowerCase();
              const filtered = !q ? labourList : labourList.filter((l) =>
                (l.name || '').toLowerCase().includes(q)
                || (l.aadhar_number || '').includes(q)
                || (l.mobile || '').includes(q)
                || (l.trade || '').toLowerCase().includes(q)
              );
              const notEnrolled = filtered.filter((l) => !l.face_enrolled);
              return (
                <>
                  {notEnrolled.length > 0 && (
                    <div className="bg-rose-50 border border-rose-200 rounded-sm p-2.5 text-[11px] text-rose-900 flex items-center gap-2">
                      <AlertTriangle size={13} className="shrink-0" />
                      <span><strong>{notEnrolled.length}</strong> labour still need face enrolment. Tap the camera button to capture.</span>
                    </div>
                  )}
                  <div className="space-y-2">
                    {filtered.map((l) => (
                      <div key={l.id} className="bg-white border border-slate-200 rounded-sm p-3 space-y-2" data-testid={`m-labour-row-${l.id}`}>
                        <div className="flex items-center gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="font-display font-bold text-sm truncate">{l.name}</div>
                            <div className="text-[11px] text-slate-500 truncate">{l.trade || '—'} · {l.mobile || 'no mobile'}</div>
                          </div>
                        </div>
                        {/* Face enrolment buttons */}
                        {l.face_enrolled ? (
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[10px] uppercase tracking-wider font-display font-bold text-emerald-700 bg-emerald-50 px-2 py-1 rounded-sm flex items-center gap-1" data-testid={`m-labour-enrolled-${l.id}`}>
                              <CheckCircle2 size={11} /> Enrolled
                            </span>
                            {!l.insightface_enrolled && (
                              <button
                                onClick={() => enrolFace(l)}
                                className="text-[10px] uppercase tracking-wider font-display font-bold text-amber-800 bg-amber-100 hover:bg-amber-200 px-2.5 py-1.5 rounded-sm flex items-center gap-1"
                                data-testid={`m-labour-reenrol-${l.id}`}
                              >
                                <Zap size={11} /> Upgrade
                              </button>
                            )}
                            <button
                              onClick={() => enrolFace(l, true)}
                              className="text-[10px] uppercase tracking-wider font-display font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 px-2.5 py-1.5 rounded-sm flex items-center gap-1 border border-slate-300"
                              data-testid={`m-labour-force-reenrol-${l.id}`}
                            >
                              <Camera size={11} /> Re-enrol
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => enrolFace(l)}
                            className="w-full text-[10px] uppercase tracking-wider font-display font-bold text-white bg-[#1F7A6E] hover:bg-[#14504E] px-3 py-1.5 rounded-sm flex items-center justify-center gap-1"
                            data-testid={`m-labour-enrol-${l.id}`}
                          >
                            <Camera size={11} /> Enrol Face
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
}

function MField({ label, value, onChange, testid, inputMode, maxLength }) {
  return (
    <div>
      <label className="text-[10px] font-display font-bold tracking-wider uppercase text-slate-500">{label}</label>
      <input type="text" value={value || ''} onChange={onChange} inputMode={inputMode} maxLength={maxLength}
        className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm mt-1 focus:outline-none focus:border-[#1F7A6E]"
        data-testid={testid} />
    </div>
  );
}
function MSelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="text-[10px] font-display font-bold tracking-wider uppercase text-slate-500">{label}</label>
      <select value={value || ''} onChange={onChange}
        className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm mt-1 bg-white focus:outline-none focus:border-[#1F7A6E]"
        data-testid={testid}>
        <option value="">— Select —</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}
function Stat({ label, value, accent }) {
  return (
    <div className="bg-slate-50 rounded-sm px-3 py-2">
      <div className="text-[9px] font-display font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-xl font-display font-bold ${accent}`}>{value}</div>
    </div>
  );
}
